// Web Push via the studio-scoped service worker. Where push is unavailable
// (iOS outside the installed app, denied permission, no VAPID key) the UI
// falls back to "add to calendar".
import { env } from './env';

export type PushSupport = 'supported' | 'needs-install' | 'unsupported' | 'denied';

export function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function pushSupport(): PushSupport {
  if (!env.vapidPublicKey) return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    const ios = /iP(hone|ad|od)/.test(navigator.userAgent);
    return ios && !isStandalone() ? 'needs-install' : 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  return 'supported';
}

function urlBase64ToUint8Array(base64: string) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

const b64url = (buf: ArrayBuffer | null) => {
  if (!buf) return '';
  let s = '';
  new Uint8Array(buf).forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** Asks permission (must be called from a user gesture) and returns subscription keys. */
export async function subscribePush(slug: string) {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Уведомления не разрешены');
  const reg = await navigator.serviceWorker.getRegistration(`/s/${slug}/`);
  if (!reg) throw new Error('Приложение ещё загружается. Попробуйте через минуту.');
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(env.vapidPublicKey),
    }));
  return { endpoint: sub.endpoint, p256dh: b64url(sub.getKey('p256dh')), auth: b64url(sub.getKey('auth')) };
}

export async function registerServiceWorker(slug: string) {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
  try {
    await navigator.serviceWorker.register(`/s/${slug}/sw.js`, { scope: `/s/${slug}/`, type: 'classic' });
  } catch {
    // offline shell is an enhancement; the site works without it
  }
}
